import * as z from 'zod';

// ─── Shared Sub-Schemas ─────────────────────────────────────────────────────

export const contentFeedFcpSchema = z.object({
  id: z.string(),
});

export const contentFeedProgramCountrySchema = z.object({
  isoCode: z.string(),
});

// ─── Media Discriminated Union ──────────────────────────────────────────────
// The upstream API provides a flat mimeType string. The proxy-api layer derives
// the `type` discriminator before forwarding to the frontend.
// TODO: Request the upstream Content Feed API to include a `type` field natively
// so the proxy-api derivation can be removed.

const contentFeedImageMediaSchema = z.object({
  type: z.literal('image'),
  url: z.string(),
  thumbnailUrl: z.string().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
  alt: z.string().optional(),
  caption: z.string().optional(),
});

const contentFeedVideoMediaSchema = z.object({
  type: z.literal('video'),
  url: z.string(),
  thumbnailUrl: z.string().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
  alt: z.string().optional(),
  caption: z.string().optional(),
});

export const contentFeedMediaResolvedSchema = z.discriminatedUnion('type', [
  contentFeedImageMediaSchema,
  contentFeedVideoMediaSchema,
]);

/** Raw media shape from the upstream API (pre-resolution). */
export const contentFeedMediaRawSchema = z.object({
  mimeType: z.string(),
  url: z.string(),
  thumbnailUrl: z.string().nullable().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
  alt: z.string().nullable().optional(),
  caption: z.string().nullable().optional(),
});

export const contentFeedFundingProgressSchema = z.object({
  /** Amount raised, in cents (minor currency units). Divide by 100 for display. */
  raised: z.number().int(),
  /** Funding goal, in cents (minor currency units). Divide by 100 for display. */
  goal: z.number().int(),
  percentageFunded: z.number().min(0).max(100),
  currency: z.string(),
  status: z.enum(['active', 'funded', 'closed']),
  fundedDate: z.string().nullable().optional(),
});

export const contentFeedMetadataSchema = z.object({
  link: z
    .object({
      url: z.string(),
    })
    .nullable()
    .optional(),
  fundingProgress: contentFeedFundingProgressSchema.nullable().optional(),
});

export const contentFeedParticipantSchema = z.object({
  localId: z.string(),
  globalId: z.string(),
});

export const contentFeedAuthorSchema = z.object({
  id: z.string(),
  displayName: z.string(),
  photo: z.object({ url: z.string() }).nullable().optional(),
  role: z.string().nullable().optional(),
});

export const contentFeedQueryMatchSchema = z.object({
  tier: z.enum(['participant', 'fcp', 'cluster', 'country']),
  related: z.object({
    participants: z.array(z.object({ localId: z.string() })).optional(),
    fcps: z.array(z.object({ id: z.string() })).optional(),
  }),
});

// ─── Reactions ──────────────────────────────────────────────────────────────

/**
 * The approved set of reaction emojis. Single source of truth for both the
 * picker UI and API validation — any emoji outside this list is rejected.
 */
export const contentFeedReactionEmojiSchema = z.enum([
  '🎉',
  '❤️',
  '🙌',
  '🙏',
  '😍',
  '👍',
]);

/** Tuple of approved emojis, for rendering the picker in list order. */
export const contentFeedReactionEmojis = contentFeedReactionEmojiSchema.options;

export const contentFeedReactionSummarySchema = z.object({
  emoji: z.string(),
  count: z.number().int(),
  userReactionId: z.string().nullable().optional(),
});

/**
 * Resolved comment shape. Declared manually because the recursive Zod schema
 * below uses `z.ZodType`, which erases inference (`z.infer` resolves to `any`).
 * This interface is the single source of truth for the comment type across the
 * API, SDK, and UI — annotate the schema with it via `z.ZodType<...>`.
 */
export interface ContentFeedCommentResolved {
  id: string;
  text: string;
  author: ContentFeedAuthor;
  status: 'pendingModeration' | 'approved';
  publishedAt: string;
  children?: ContentFeedCommentResolved[];
  reactions?: ContentFeedReactionSummary[];
}

export const contentFeedCommentResolvedSchema: z.ZodType<ContentFeedCommentResolved> =
  z.lazy(() =>
    z.object({
      id: z.string(),
      text: z.string(),
      author: contentFeedAuthorSchema,
      status: z.enum(['pendingModeration', 'approved']),
      publishedAt: z.string(),
      children: z.array(contentFeedCommentResolvedSchema).optional(),
      reactions: z.array(contentFeedReactionSummarySchema).optional(),
    }),
  );

// ─── Entry Schemas ──────────────────────────────────────────────────────────

/** Entry shape as returned by the upstream API (media uses raw mimeType). */
export const contentFeedEntryRawSchema = z.object({
  id: z.string(),
  source: z.enum(['community', 'causes', 'compassion.media', 'cause-updates']),
  locales: z.array(z.string()),
  title: z.string().nullable(),
  description: z.string().nullable(),
  fcp: contentFeedFcpSchema.nullable().optional(),
  programCountry: contentFeedProgramCountrySchema.nullable().optional(),
  media: z.array(contentFeedMediaRawSchema).nullable().optional(),
  metadata: contentFeedMetadataSchema.nullable().optional(),
  participants: z.array(contentFeedParticipantSchema).nullable().optional(),
  author: contentFeedAuthorSchema.nullable().optional(),
  reactions: z.array(contentFeedReactionSummarySchema).optional(),
  canComment: z.boolean().optional(),
  canReact: z.boolean().optional(),
  topComment: contentFeedCommentResolvedSchema.nullable().optional(),
  commentCount: z.number().int().optional(),
  publishedAt: z.string(),
});

export const contentFeedEntryRawListItemSchema =
  contentFeedEntryRawSchema.extend({
    match: contentFeedQueryMatchSchema.optional(),
  });

/** Raw response shape from the upstream API. */
export const contentFeedEntryRawListResponseSchema = z.object({
  entries: z.array(contentFeedEntryRawListItemSchema),
  cursor: z.string().nullable(),
});

/** Resolved entry shape for frontend consumption (media uses discriminated type). */
export const contentFeedEntryResolvedSchema = z.object({
  id: z.string(),
  source: z.enum(['community', 'causes', 'compassion.media', 'cause-updates']),
  locales: z.array(z.string()),
  title: z.string().nullable(),
  description: z.string().nullable(),
  fcp: contentFeedFcpSchema.optional(),
  programCountry: contentFeedProgramCountrySchema.optional(),
  media: z.array(contentFeedMediaResolvedSchema).optional(),
  metadata: contentFeedMetadataSchema.optional(),
  participants: z.array(contentFeedParticipantSchema).optional(),
  author: contentFeedAuthorSchema.optional(),
  reactions: z.array(contentFeedReactionSummarySchema).optional(),
  canComment: z.boolean().optional(),
  canReact: z.boolean().optional(),
  topComment: contentFeedCommentResolvedSchema.nullable().optional(),
  commentCount: z.number().int().optional(),
  publishedAt: z.string(),
});

export const contentFeedEntryResolvedListItemSchema =
  contentFeedEntryResolvedSchema.extend({
    match: contentFeedQueryMatchSchema.optional(),
  });

// ─── Response Schema ────────────────────────────────────────────────────────

export const contentFeedEntryListResponseSchema = z.object({
  entries: z.array(contentFeedEntryResolvedListItemSchema),
  cursor: z.string().nullable(),
});

// ─── Search Request Body Schema ─────────────────────────────────────────────

const contentFeedSourceEnum = z.enum([
  'community',
  'causes',
  'compassion.media',
  'cause-updates',
]);

export const contentFeedSearchRequestSchema = z.object({
  locale: z.string().regex(/^[a-z]{2,3}(-[A-Z]{2})?$/),
  supporterGlobalId: z.string().optional(),
  participantLocalIds: z.array(z.string()).optional(),
  fcpIds: z.array(z.string().regex(/^[A-Z]{2}\d{4}$/)).optional(),
  programCountryIsoCodes: z.array(z.string().regex(/^[A-Z]{2}$/)).optional(),
  sources: z.array(contentFeedSourceEnum).max(100).optional(),
  excludeSources: z.array(contentFeedSourceEnum).max(100).optional(),
  publishedBefore: z.string().datetime().optional(),
  publishedAfter: z.string().datetime().optional(),
  includeFinancialAppeals: z.boolean().optional(),
  includeNearbyEntries: z.boolean().optional(),
  sortBy: z.enum(['publishedAt', 'proximity']).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
  limit: z.number().int().min(1).max(100).optional(),
  cursor: z.string().min(1).optional(),
});

// ─── Reaction Request/Response Schemas ──────────────────────────────────────

/**
 * Reaction identifier, as returned by the create endpoint.
 * Validated before interpolation into upstream URLs so a malformed path
 * segment can't reshape the request.
 */
export const contentFeedReactionIdSchema = z.string().uuid();

/**
 * Body for POST /api/content-feed/reactions.
 * The route handler must parse with this schema before forwarding upstream —
 * the emoji enum is the enforcement point against arbitrary client input.
 * Exactly one of entryId or commentId is required.
 */
export const contentFeedReactionCreateRequestSchema = z
  .object({
    emoji: contentFeedReactionEmojiSchema,
    entryId: z.string().uuid().optional(),
    commentId: z.string().uuid().optional(),
  })
  .refine((data) => data.entryId || data.commentId, {
    message: 'Either entryId or commentId is required',
  });

export const contentFeedReactionCreateResponseSchema = z.object({
  id: contentFeedReactionIdSchema,
  emoji: z.string(),
  entryId: z.string().uuid().optional(),
  commentId: z.string().uuid().optional(),
});

// ─── Comment Request/Response Schemas ───────────────────────────────────────

/**
 * Maximum length of a content feed comment body. Single source of truth for the
 * API contract — enforced by the request schema below and reused by the UI for
 * its character counter and submit gating.
 */
export const contentFeedCommentMaxLength = 1000;

/**
 * Body for POST /api/content-feed/comments.
 * Creates a new comment on an entry or a reply to an existing comment.
 * Exactly one of entryId or parentId is required.
 */
export const contentFeedCommentCreateRequestSchema = z
  .object({
    locale: z.string().regex(/^[a-z]{2,3}(-[A-Z]{2})?$/),
    text: z.string().min(1).max(contentFeedCommentMaxLength),
    entryId: z.string().uuid().optional(),
    parentId: z.string().uuid().optional(),
  })
  .refine((data) => data.entryId || data.parentId, {
    message: 'Either entryId or parentId is required',
  });

/** Response from creating a comment. */
export const contentFeedCommentCreateResponseSchema = z.object({
  id: z.string().uuid(),
  locales: z.array(z.string()),
  text: z.record(z.string(), z.string()),
  author: contentFeedAuthorSchema,
  entryId: z.string().uuid().nullable().optional(),
  parentId: z.string().uuid().nullable().optional(),
  status: z.enum(['pendingModeration', 'approved', 'rejected']),
});

/** Paginated response for listing comments on an entry. */
export const contentFeedCommentListResponseSchema = z.object({
  comments: z.array(contentFeedCommentResolvedSchema),
  cursor: z.string().nullable(),
});

/** Query parameters for fetching comments on an entry. */
export const contentFeedCommentListRequestSchema = z.object({
  entryId: z.string().uuid(),
  locale: z.string().regex(/^[a-z]{2,3}(-[A-Z]{2})?$/),
  supporterGlobalId: z.string().optional(),
  limit: z.number().int().min(1).max(100).optional(),
  cursor: z.string().min(1).optional(),
});

// ─── Profile Request/Response Schemas ───────────────────────────────────────

export const contentFeedProfilePatchRequestSchema = z.object({
  displayName: z.string().min(1),
});

export const contentFeedProfileResponseSchema = z.object({
  id: z.string(),
  supporterGlobalId: z.string(),
  displayName: z.string(),
});

// ─── Inferred TypeScript Types ──────────────────────────────────────────────

export type ContentFeedMedia = z.infer<typeof contentFeedMediaResolvedSchema>;
export type ContentFeedMediaRaw = z.infer<typeof contentFeedMediaRawSchema>;
export type ContentFeedFundingProgress = z.infer<
  typeof contentFeedFundingProgressSchema
>;
export type ContentFeedMetadata = z.infer<typeof contentFeedMetadataSchema>;
export type ContentFeedParticipant = z.infer<
  typeof contentFeedParticipantSchema
>;
export type ContentFeedAuthor = z.infer<typeof contentFeedAuthorSchema>;
export type ContentFeedQueryMatch = z.infer<typeof contentFeedQueryMatchSchema>;
export type ContentFeedReactionEmoji = z.infer<
  typeof contentFeedReactionEmojiSchema
>;
export type ContentFeedReactionSummary = z.infer<
  typeof contentFeedReactionSummarySchema
>;
export type ContentFeedReactionCreateRequest = z.infer<
  typeof contentFeedReactionCreateRequestSchema
>;
export type ContentFeedReactionCreateResponse = z.infer<
  typeof contentFeedReactionCreateResponseSchema
>;
export type ContentFeedProfilePatchRequest = z.infer<
  typeof contentFeedProfilePatchRequestSchema
>;
export type ContentFeedProfileResponse = z.infer<
  typeof contentFeedProfileResponseSchema
>;
// `ContentFeedCommentResolved` is declared as an interface above (co-located
// with its recursive schema) — no `z.infer` alias needed here.
export type ContentFeedEntryResolved = z.infer<
  typeof contentFeedEntryResolvedSchema
>;
export type ContentFeedEntryResolvedListItem = z.infer<
  typeof contentFeedEntryResolvedListItemSchema
>;
export type ContentFeedEntryListResponse = z.infer<
  typeof contentFeedEntryListResponseSchema
>;
export type ContentFeedEntryRawListResponse = z.infer<
  typeof contentFeedEntryRawListResponseSchema
>;
export type ContentFeedSearchRequest = z.infer<
  typeof contentFeedSearchRequestSchema
>;
export type ContentFeedCommentCreateRequest = z.infer<
  typeof contentFeedCommentCreateRequestSchema
>;
export type ContentFeedCommentCreateResponse = z.infer<
  typeof contentFeedCommentCreateResponseSchema
>;
export type ContentFeedCommentListResponse = z.infer<
  typeof contentFeedCommentListResponseSchema
>;
export type ContentFeedCommentListRequest = z.infer<
  typeof contentFeedCommentListRequestSchema
>;
